import { changeTheme } from "./appStyle"
import { i18n } from "../lang/i18n"
import type { AidenResponse, Config, Control, IWebshopAPI, ResultsControl } from "../types"
import { storage } from "./storage/storage"
import { IntegrationsAnalytics } from "../../analytics/integrations/IntegrationsAnalytics"

const init = (
  webshopAPI: IWebshopAPI,
  advisorId: string,
  isPreview: boolean,
  config: Config,
  testVariant: string | null,
  sessionId: string | null,
  setupCallback?: (d: AidenResponse | undefined) => void,
  errorCallback?: (e: unknown) => void
): void => {
  const stored = isPreview ? null : storage.get(advisorId)

  if (!stored || config.resetSessionOnStart) {
    webshopAPI
      .init(advisorId, isPreview, config, testVariant, sessionId)
      .then((d: AidenResponse | undefined) => {
        setupCallback && setupCallback(d)
        return d
      })
      .catch((e: unknown) => {
        if (e instanceof Error) {
          if (e.message !== "Failed to fetch") {
            console.error(e)
          }
        }
        errorCallback && errorCallback(e)
      })
  } else if (
    productScopeChanged(
      stored.currentScreenId,
      stored.productFocus,
      stored.screen.controls,
      config
    )
  ) {
    const requestData = {
      ...stored,
      screenId: stored.currentScreenId,
      clientInfo: { userAgent: navigator.userAgent },
    }
    webshopAPI
      .refreshAdviceScreen(advisorId, isPreview, config, requestData)
      .then((response: AidenResponse | undefined) => {
        if (response) {
          IntegrationsAnalytics().adviceChanged(advisorId, response)
        }
        setupCallback && setupCallback(response)
        return response
      })
      .catch((e: unknown) => {
        if (e instanceof Error) {
          if (e.message !== "Failed to fetch") {
            console.error(e)
          }
        }
        errorCallback && errorCallback(e)
      })
  } else {
    setupCallback && setupCallback(stored)
  }
}
const productScopeChanged = (
  currentScreenId: string,
  lastUsedProductFocus: AidenResponse["productFocus"],
  controls: Control[],
  config: Config
): boolean => {
  const isAdviceScreen = currentScreenId === "ADVICE"
  const pdp = controls.find((ctrl) => ctrl.component === "pdp-card")
  const hasResults = !!(
    controls.find((ctrl) => ctrl.component === "results") as
      | ResultsControl
      | undefined
  )?.controls.length
  const adviceIsProductFocussed = !!pdp
  const productScopeRequested = !!config.productScope

  if (isAdviceScreen === false || !hasResults) return false
  if (adviceIsProductFocussed && !productScopeRequested) return true
  if (!adviceIsProductFocussed && productScopeRequested) return true

  if (config.productScope && adviceIsProductFocussed) {
    if (config.productScope.type === "product-url") {
      return config.productScope.url !== lastUsedProductFocus
    }
    if (config.productScope.type === "product-id") {
      return config.productScope.id !== lastUsedProductFocus
    }
  }
  return false
}

const setupConfig = (d: AidenResponse): void => {
  changeTheme(d.styleSheet)
  i18n.setupI18n({ withLocale: d.locale })
}

export const initializeAdvisor = (
  webshopAPI: IWebshopAPI,
  advisorId: string,
  isPreview: boolean,
  config: Config,
  callback: (d: AidenResponse | undefined) => void,
  errorCallback?: (e: unknown) => void
): void => {
  init(
    webshopAPI,
    advisorId,
    isPreview,
    config,
    null,
    null,
    (d: AidenResponse | undefined) => {
      if (d) {
        setupConfig(d)
      }
      callback && callback(d)
    },
    errorCallback
  )
}

export const resetAdvisor = (
  webshopAPI: IWebshopAPI,
  advisorId: string,
  isPreview: boolean,
  config: Config,
  sessionId: string | null,
  testVariant: string | null,
  callback: (d: AidenResponse | undefined) => void,
  errorCallback?: (e: unknown) => void
): void => {
  storage.remove(advisorId)
  init(
    webshopAPI,
    advisorId,
    isPreview,
    config,
    sessionId,
    testVariant,
    (d: AidenResponse | undefined) => {
      d && setupConfig(d)
      callback && callback(d)
    },
    errorCallback
  )
}
